import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb-common/interfaces/grid-layout.service";
import {DeleteHeaderComponent} from "@irb/irb-common/components/grid/renderers/delete.header.component";
import {DeleteCellComponent} from "@irb/irb-common/components/grid/renderers/delete.cell.component";
import {NumberTextBoxEditorComponent} from "@irb-common/components/grid/editors/number-text-box-editor.component";
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import {DropdownFilterRendererComponent} from "@irb-common/components/grid/renderers/dropdown-filter.cell.component";
import {IDropdownItem} from "@irb-common/models/dropdown.model";
import {ReplaceSecuritiesRebalSettingsComponent} from "@irb/irb-editors/editor-replace-securities-rebalance/replace-securities-rebal-settings/replace-securities-rebal-settings.component";
import {IrbAppEvents, IrbEditorsEvents} from "@irb/irb-common/events";
import {AppService} from "@irb/app.service";
import {CheckboxCellComponent} from '@irb-common/components/grid/renderers/checkbox.cell.component';

@Injectable({
  providedIn: "root",
})
export class ReplaceSecuritiesRebalSettingsGridLayoutService implements GridLayoutService<ReplaceSecuritiesRebalSettingsComponent> {
  constructor(private as: AppService) {}

  getFrameworkComponents() {
    return {
      "DeleteHeaderComponent": DeleteHeaderComponent,
      "DeleteCellComponent": DeleteCellComponent,
      "CheckboxCellComponent": CheckboxCellComponent,
      "NumberTextBoxEditorComponent": NumberTextBoxEditorComponent,
      "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
      "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent
    };
  }
  maxGainColInfo(){
    return `
          <p>
            You can choose to enter a maximum gain threshold for short-term gains or all gains.
            iRebal will stop proposing sales in taxable accounts with gains when 
            this threshold is reached (iRebal will always sell tax lots with losses first). 
            If you leave this threshold blank, iRebal will sell the desired amount regardless of gains. 
            If you set the short-term threshold to 0%, iRebal will not allow any short-term gains.
            If you set the total gain threshold to 0%, iRebal willl not allow any gains at all.
          </p> `;
  }

  replaceWithColInfo(){
    return `
          <p>
          You can tell iRebal to purchase a security in place of the security being sold, 
          or you choose to leave the proceeds of the sale in cash. You may select
           multiple replacement securities for each security being replaced. To add 
           multiple replacements, select "Multiple Replacements" from the drop down
           and enter your replacement securities and the % of proceeds that should be
           used for each
          </p> `;
  }

  getColumnDefs(parent: ReplaceSecuritiesRebalSettingsComponent) {
    return [
      {
        headerName: "",
        headerComponent: "DeleteHeaderComponent",
        headerComponentParams: {
          message: "Remove all items?",
          proceed: parent.deleteAll.bind(parent),
          cancel: parent.cancelDelete.bind(parent),
        },
        cellRenderer: "DeleteCellComponent",
        cellRendererParams: {
          message: "Remove this item?",
          proceed: parent.delete.bind(parent),
          cancel: parent.cancelDelete.bind(parent),
        },
        resizable: false,
        minWidth: 25,
        maxWidth: 25,
        width: 25,
      },
      {
        headerName: "Sell",
        children: [
          {
            headerName: "Symbol",
            field: "securitySymb",
            minWidth: 100,
            flex: 1,
            sort: "asc",
            suppressFiltersToolPanel: true,
          },
          {
            headerName: "% to Sell",
            headerComponentParams: {
              irbColInfo: `<p>Enter a % of the current security holding to sell. Examples: entering 100% tells iRebal to sell all of the holding; entering 50% tells iRebal to sell half of the holding.</p>`
            },
            field: "toSellPercent",
            minWidth: 80,
            flex: 1,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    onChange: (params: any, c: any) => {
                        if (params && params.data.toSellPercent != c.value && c.isValid) {
                            params.data.toSellPercent = parseFloat(c.value);
                            params.api.redrawRows({ rowNodes: [params.node] });
                        }
                    },
                };
            }
          },
        ],
      },
      {
        headerName: "Max Gain",
        headerGroupComponent: 'customHeaderGroup',
        headerGroupComponentParams: {
          irbColInfo: this.maxGainColInfo()
      },
        children: [
          {
            headerName: "ST %",
            field: "shortTermGainPercent",
            //editable: true,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    onChange: (params: any, c: any) => {
                        if (params && params.data.shortTermGainPercent != c.value && c.isValid) {
                            params.data.shortTermGainPercent = parseFloat(c.value);
                            params.api.redrawRows({ rowNodes: [params.node] });
                        }
                    },
                };
            }
          },
          {
            headerName: "Total %",
            field: "totalGainPercent",
            //editable: true,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    onChange: (params: any, c: any) => {
                        if (params && params.data.totalGainPercent != c.value && c.isValid) {
                            params.data.totalGainPercent = parseFloat(c.value);
                            params.api.redrawRows({ rowNodes: [params.node] });
                        }
                    },
                };
            }
          },
        ],
      },
      {
        headerName: "Replace with",
        headerGroupComponent: 'customHeaderGroup',
        headerGroupComponentParams: {
           irbColInfo: this.replaceWithColInfo()
        },
        children: [
          {
            headerName: "Symbol",
            field: "replacedSymbol",
            minWidth: 170,
            flex: 1,
            cellClass: "dropdownFilterCell",
            cellRenderer: "DropdownFilterRendererComponent",
            cellRendererParams: (params: any) => {
              parent.originalValue = params.data.replacedSymbol;
              const addNewItemConfig = {
                button: 'Add new security',
                type: 'security',
                fromComp: 'ReplaceSecuritiesRebalSettingsGridLayoutService',
                rowIndex: params.rowIndex || params.node.rowIndex
            };
              if(!params.data.replacedSymbol) {
                params.data.replacedSymbol = "None (leave in cash)";
              }

              return {
                defaultValue: { 
                    name: params.data.replacedSymbol, 
                    value: (parent.secuitySymbolMap.get(params.data.replacedSymbol)) ? parent.secuitySymbolMap.get(params.data.replacedSymbol).securityId : null
                },
                values: parent.securitySymbolsDD,
                addNewItemConfig: addNewItemConfig,
                onChange: (params: any, item: IDropdownItem) => {
                  params.data.replacedSymbol = item.name;
                  if (params.data.replacedSymbol.indexOf("Multiple replacements") !== -1) {
                    const dt: any = {
                      data: params.data,
                      replacements: [],
                    };
                    this.as.fireEvent(IrbEditorsEvents.SHOW_BUY_MULTIPLE_REPLACEMENT, dt);
                    params.data.replacedSymbol = parent.originalValue;
                  } else if (params.data.replacedSymbol === params.data.securitySymb) {
                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                      severity: "warn",
                      summary: "",
                      detail: "Symbol " + params.data.securitySymb + " can not be listed as a replacement buy security for " + params.data.securitySymb
                    });
                    params.data.replacedSymbol = parent.originalValue;
                  } else {
                    const s = parent.secuitySymbolMap.get(item.name);
                    params.data.replacedType = s.securityType;
                    const replaceSecurityItem: any = {
                      multipleSecuritiesId : '',
                      securityId : s.securityId,
                      securitySymb : s.securitySymbol,
                      securityType: s.securityType,
                      allocationPercent : "100",
                      replaceMultipleSecuritiesId : "",
                      isUIdeleted : false,
                      isNew: true,
                      isDeleted: false,
                      isSwapExchange: null
                    };

                    params.data.mutipleSecurityAllocations = parent.setMutipleSecurityAllocations(params.data.mutipleSecurityAllocations, replaceSecurityItem);
                    parent.warnIfSymbolUsedAsEquityReplaceTarget(params.data.replacedSymbol);
                  }
                  
                  params.api.redrawRows({ rowNodes: [params.node] });
                   parent.onAllowInModified();
                },
                openNewItemFlow: (dataParam: any) => {
                  dataParam = Object.assign({}, addNewItemConfig, dataParam);
                  parent.openNewItemSecurityFlow(dataParam, true);
              },
                onClearSelection: (params: any) => {
                  params.data.replacedSymbol = "None (leave in cash)";
                  params.data.replacedType = null;
                  params.data.mutipleSecurityAllocations = parent.setMutipleSecurityAllocations(params.data.mutipleSecurityAllocations, null);
                  params.api.redrawRows({ rowNodes: [params.node] });
                },
              };
            },
            suppressFiltersToolPanel: true,
            cellStyle: { cursor: "pointer" },
          },
          {
            headerName: "",
            editable: false,
            minWidth: 100,
            suppressFiltersToolPanel: true,
            valueGetter: (params: any) => {
              if(params.data.replacedSymbol) {
                return params.data.replacedSymbol.indexOf("(multiple)") !== -1 ? "view" : "";
              } else {
                return "";
              }
            },
            cellRenderer: "cellRendererFormatter",
            cellRendererParams: {
              link: (params: any) => {
                return params.data.mutipleSecurityAllocations && params.data.mutipleSecurityAllocations.length > 1;
              },
              onClick: function (data: any) {
                const dt: any = {
                  data: data,
                  replacements: data.mutipleSecurityAllocations, // not sure if it correct place
                };
                this.as.fireEvent(IrbEditorsEvents.SHOW_BUY_MULTIPLE_REPLACEMENT, dt);
              }.bind(this),
            },
          },
        ],
      },
      {
        headerName: "",
        children: [
          {
            headerName: "Cap Buy %",
            field: "capType",
            minWidth: 130,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "dropdownFilterCell",
            cellRenderer: "DropdownFilterRendererComponent",
            cellRendererParams: (params: any) => {
              return {
                defaultValue: parent.getDefaultValueForCapType(params.data.capType),
                values: parent.capBuyPer,
                onChange: (params: any, item: IDropdownItem) => {
                  params.data.capType = item.value;

                  if (params.data.capType === "MODEL_PCT" || params.data.capType === "NONE") {
                    params.data.capPercent = null;
                  } else {
                    params.data.capPercent = params.data.capPercent || 0;
                    setTimeout(() => {
                      const elem: any = document.querySelectorAll(".ag-row-focus .capBuyClassForFocus input");

                      if (elem && elem.length !== 0) {
                        elem[0].focus();
                      }
                    }, 100);
                  }

                  params.api.redrawRows({ rowNodes: [params.node] });
                  parent.onAllowInModified();                
                },
                onClearSelection: (params: any) => {
                  params.data.capType = "NONE";
                  params.data.capPercent = null;
                  params.api.redrawRows({ rowNodes: [params.node] });
                }
              };
            },
          },
          {
            headerName: "",
            field: "capPercent",
            //editable: parent.isCapPercentEditable,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "numberTextBoxCell capBuyClassForFocus",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: (params: any) => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    disabled: !parent.isCapPercentEditable(params),
                    onChange: (params: any, c: any) => {
                        if (params && params.data.capPercent != c.value && c.isValid) {
                            params.data.capPercent = parseFloat(c.value);
                            params.api.redrawRows({ rowNodes: [params.node] });
                        }
                    },
                };
            }
          },
        ],
      },
      {
        headerName: "",
        headerGroupComponent: 'customHeaderGroup',
        children: [
          {
            headerName: "Swap/Exch",
            field: "isSwapExchange",
            minWidth: 100,
            flex: 1,
            cellRenderer: "CheckboxCellComponent",
            cellRendererParams: (params: any) => {
              const isEquityReplaceTarget = parent.checkIfSymbolUsedAsEquityReplaceTarget(params.data.replacedSymbol);
              let replacedType = null;
              // const replacedType =parent.secuitySymbolMap.get(params.data.replacedSymbol).securityType;
              // const swapExchNotAllowed = params.data.securityType !== 'MUTUAL_FUND' || params.data.replacedType !== 'MUTUAL_FUND' || isEquityReplaceTarget;
               let allocations =   JSON.parse(JSON.stringify(params.data.mutipleSecurityAllocations));
               if(!allocations) {
                 allocations = [];
               }
              let notDeletedAllocations = allocations.filter((x:any)=>!x.isUIdeleted);
               if(notDeletedAllocations.length === 1) {
                 replacedType = notDeletedAllocations[0].securityType;
               } else {
                 replacedType = '';
               }
              const swapExchNotAllowed = params.data.securityType !== 'MUTUAL_FUND' || replacedType !== 'MUTUAL_FUND' ||isEquityReplaceTarget;

              if(swapExchNotAllowed && params.data.isSwapExchange) {
                params.data.isSwapExchange = false;
              }

              return {
                value: params.data.isSwapExchange,
                isDisabled: swapExchNotAllowed,
                onChange: (newValue: boolean) => {
                  params.data.isSwapExchange = newValue;
                  parent.onAllowInModified();
                }
              }
            },
            suppressFiltersToolPanel: true,
            cellStyle: { "text-align": "center" }
          },
        ],
      },
    ];
  }

  getDisabledColumnDefs(parent: ReplaceSecuritiesRebalSettingsComponent) {
    return [
      {
        headerName: "",
        editable: false,
        headerComponent: "DeleteHeaderComponent",
        headerComponentParams: {
          message: "Remove all items?",
          proceed: parent.deleteAll.bind(parent),
          cancel: parent.cancelDelete.bind(parent),
          disableDelete: parent.isGridEditable.bind(parent),
        },
        cellRenderer: "DeleteCellComponent",
        cellRendererParams: {
          message: "Remove this item?",
          proceed: parent.delete.bind(parent),
          cancel: parent.cancelDelete.bind(parent),
          disableDelete: parent.isGridEditable,
        },
        resizable: false,
        minWidth: 25,
        maxWidth: 25,
        width: 25,
      },
      {
        headerName: "Sell",
        children: [
          {
            headerName: "Symbol",
            field: "securitySymb",
            minWidth: 100,
            flex: 1,
            sort: "asc",
            suppressFiltersToolPanel: true,
          },
          {
            headerName: "% to Sell",
            headerComponentParams: {
              irbColInfo: `<p>Enter a % of the current security holding to sell. Examples: entering 100% tells iRebal to sell all of the holding; entering 50% tells iRebal to sell half of the holding.</p>`
            },
            field: "toSellPercent",
            //editable: false,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    disabled: true
                };
            }
          },
        ],
      },
      {
        headerName: "Max Gain",
        headerGroupComponent: 'customHeaderGroup',
        headerGroupComponentParams: {
          irbColInfo: this.maxGainColInfo()
      },
        children: [
          {
            headerName: "ST %",
            field: "shortTermGainPercent",
            //editable: false,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    disabled: true
                };
            }
          },
          {
            headerName: "Total %",
            field: "totalGainPercent",
            //editable: false,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    disabled: true
                };
            }
          },
        ],
      },
      {
        headerName: "Replace with",
        headerGroupComponent: 'customHeaderGroup',
        headerGroupComponentParams: {
           irbColInfo: this.replaceWithColInfo()
        },
        children: [
          {
            headerName: "Symbol",
            field: "replacedSymbol",
            minWidth: 150,
            flex: 1,
            editable: false,
            cellRendererParams: {
              values: parent.securitySymbolsDD,
            },
            cellRenderer: "DropdownSearchRendererComponent",
            cellClass: "dropdownSearchCell",
            suppressFiltersToolPanel: true,
            cellStyle: { cursor: "pointer" },
          },
          {
            headerName: "",
            editable: false,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            valueGetter: (params: any) => {
              let val: any = '';
              if (params.data && params.data.replacedSymbol) {
                val = params.data.replacedSymbol.indexOf("(multiple)") !== -1 ? "view" : "";
              }
              return val;
            },
            cellRenderer: "cellRendererFormatter",
            cellRendererParams: {
              link: (params: any) => {
                return params.data.mutipleSecurityAllocations && params.data.mutipleSecurityAllocations.length > 1;
              },
              onClick: function (data: any) {
                const dt: any = {
                  data: data,
                  isReadOnly: this.isReadOnly,
                  replacements: data.mutipleSecurityAllocations, // not sure if it correct place
                };
                this.as.fireEvent(IrbEditorsEvents.SHOW_BUY_MULTIPLE_REPLACEMENT, dt);
              }.bind(this),
            },
          },
        ],
      },
      {
        headerName: "",
        children: [
          {
            headerName: "Cap Buy %",
            field: "capType",
            minWidth: 130,
            flex: 1,
            editable: false,
            valueFormatter: (params: any) => {
              let cap:any= "None";
              const val = parent.capBuyPer.filter(x=>x.value===params.value);
              if (val.length !== 0) {
                cap=val[0].name;
              }
              return cap;
            },
            suppressFiltersToolPanel: true,
            cellStyle: { cursor: "pointer" },
          },
          {
            headerName: "",
            field: "capPercent",
            //editable: false,
            minWidth: 80,
            flex: 1,
            suppressFiltersToolPanel: true,
            cellClass: "numberTextBoxCell",
            cellRenderer: "NumberTextBoxRendererComponent",
            cellRendererParams: () => {
                return {
                    min: 0,
                    max: 100,
                    places: 2,
                    isPercentage: true,
                    disabled: true
                };
            }
          },
        ],
      },
      {
        headerName: "",
        headerGroupComponent: 'customHeaderGroup',
        children: [
          {
            headerName: "Swap/Exch",
            field: "isSwapExchange",
            minWidth: 100,
            flex: 1,
            cellRenderer: "CheckboxCellComponent",
            cellRendererParams: (params: any) => {
              return {
                value: params.data.isSwapExchange,
                isDisabled: true
              }
            },
            suppressFiltersToolPanel: true,
            cellStyle: { "text-align": "center" }
          },
        ],
      },
    ];
  }
}
